// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
// Services
import { ConfigService, SystemApiService } from '@cv-invision/core';
// Others
import { Observable, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
// Model
import { RULE_STATUS } from '../../rule-engine/rules/models';
import { StaticMapConfig, StaticMapRequest, StaticMarker } from '../models/map-service.model';

@Injectable({providedIn: 'root'})
export class ExternalApiService {
	protected desktopUrl = this.systemApiService.api.desktopService;
	protected desktopUrlOrders = `${this.desktopUrl}orders/`
	protected desktopUrlRules = `${this.desktopUrl}rules/`
	protected desktopUrlUtils = `${this.desktopUrl}utils`

	constructor(
		private httpClient: HttpClient,
		private systemApiService: SystemApiService,
		private configService: ConfigService
	) {
	}

	/**
	 * Get Attachments list
	 * @param orderID
	 * @param postData
	 */
	public getAttachments(orderID: any, refJsonPath?: string): Observable<any> {
		const url = `${this.desktopUrlOrders}${orderID}/document/attachments`;
		const params = [];

		if (refJsonPath) {
			params.push(`jsonPath=${refJsonPath}`);
		}

		return this.httpClient
			.get(`${url}?${params.join('&')}`)
			.pipe(map((payload: any) => payload));
	}

	/**
	 * Create Attachment
	 * @param orderID
	 * @param postData
	 */
	public createAttachment(orderID: any, postData: any): Observable<any> {

		const formData = new FormData();
		formData.append('Name', postData.name);
		formData.append('JsonPath', postData.jsonPath);
		formData.append('FileData', new File([ postData.fileData ], postData.name, {type: 'image/jpeg'}));
		formData.append('FileMetadata.Type', postData.photoType);
		formData.append('FileMetadata.ImageType', 'HDR');
		formData.append('FileMetadata.IsPrimary', 'false');
		formData.append('FileMetadata.Latitude', postData.latitude);
		formData.append('FileMetadata.Longitude', postData.longitude);
		formData.append('FileMetadata.TimeSpan', Math.floor(Date.now() / 1000).toString());

		return this.httpClient.post(`${this.desktopUrlOrders}${orderID}/document/attachments/photos`, formData).pipe(
			map((response: any) => response)
		);
	}

	/**
	 * Create Attachment
	 * @param orderID
	 * @param postData
	 */
	public updateAttachment(orderID: string, attachmentId: string, file: any): Observable<any> {

		const formData = new FormData();
		formData.append('FileData', file);

		return this.httpClient.put(`${this.desktopUrlOrders}${orderID}/document/attachments/${attachmentId}/blob`, formData).pipe(
			map((response: any) => response)
		);
	}

	/**
	 * Create Smart Tag with Attachment
	 * @param orderID
	 * @param postData
	 */
	public createSmartTagWithAttachment(orderID: any, postData: any): Observable<any> {

		const formData = new FormData();
		formData.append('JsonDocument', JSON.stringify(postData.smartTagData));
		formData.append('Name', postData.name);
		formData.append('JsonPath', postData.jsonPath);
		formData.append('FileData', new File([ postData.fileData ], postData.name, {type: 'image/jpeg'}));
		formData.append('FileMetadata.Type', 'ROOM');
		formData.append('FileMetadata.ImageType', 'NONE');
		formData.append('FileMetadata.IsPrimary', 'false');
		formData.append('FileMetadata.Latitude', postData.latitude);
		formData.append('FileMetadata.Longitude', postData.longitude);
		formData.append('FileMetadata.TimeSpan', Math.floor(Date.now() / 1000).toString());

		return this.httpClient.post(
			`${this.desktopUrlOrders}${orderID}/document/attachments/photos/smarttags`, formData).pipe(
			map((response: any) => response)
		);
	}

	/**
	 * Create Smart Tag with Attachment
	 *
	 * @param orderID
	 * @param documentUpdates
	 * @param etag
	 */
	public patchDocument(orderID: any, documentUpdates: any, etag: string): Observable<any> {

		const options: any = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				'If-Match': etag
			}),
			observe: 'response'
		};

		const postData = {
			updates: documentUpdates
		}

		return this.httpClient.patch(
			`${this.desktopUrlOrders}${orderID}/document`, postData, options).pipe(
			map((resp: any) => {
				// This request contains an etag signature
				const etag = resp.headers.get('etag');
				return {body: resp.body, etag: etag};
			}),
			catchError((err: any) => {
				if (err.code === 412) {
					return {invalidOrder: true};
				}
				return err;
			})
		);
	}

	/**
	 * Get the Attachment by the Order and ID
	 * @param orderID
	 * @param attachmentID
	 */
	public getAttachmentById(orderID: any, attachmentID: any): Observable<any> {
		return this.httpClient.get(`${this.desktopUrlOrders}${orderID}/document/attachments/${attachmentID}`).pipe(
			map((response: any) => response)
		);
	}

	/**
	 * Get the Attachment by the Order and ID
	 * @param orderID
	 * @param attachmentID
	 */
	public saveBlurAttachmentById(orderID: any, attachmentID: any, etag: string, payload: any): Observable<any> {

		const options: any = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				'If-Match': etag
			}),
			observe: 'response'
		};

		return this.httpClient.put(
			`${this.desktopUrlOrders}${orderID}/document/attachments/${attachmentID}/updateBlurAttachment`, payload, options).pipe(
			map((response: any) => response.body)
		);
	}

	/**
	 * Delete the Attachment by the Order and ID
	 * @param orderID
	 * @param attachmentID
	 */
	public deleteAttachmentById(orderID: any, attachmentID: any): Observable<any> {
		return this.httpClient.delete(`${this.desktopUrlOrders}${orderID}/document/attachments/${attachmentID}`)
			.pipe(
				map((response: any) => {
					return {
						...response,
						deleted: true
					};
				})
			);
	}

	/**
	 * Delete the Attachment by the Order and ID
	 * @param orderID
	 * @param attachmentIds
	 */
	public deleteAttachmentsBulk(orderID: any, attachmentIds: string[]): Observable<any> {
		const payload = {
			attachmentIds,
			npe: false
		};
		return this.httpClient.delete(`${this.desktopUrlOrders}${orderID}/document/attachments/`, { body: payload })
			.pipe(
				map((response: any) => {
					return {
						...response,
						deleted: true
					};
				})
			);
	}

	/**
	 * Delete the Attachment by the Order and ID
	 * @param orderID
	 * @param attachmentID
	 */
	public deleteSmartTagById(orderID: any, smartTagId: any): Observable<any> {
		return this.httpClient.delete(
			`${this.desktopUrlOrders}${orderID}/document/attachments/photos/smarttags/${smartTagId}`
		).pipe(
			map((response: any) => {
				return {
					...response,
					deleted: true
				};
			})
		);
	}

	// Rules -------------------------------------
	/**
	 * POST - Create a New Rule
	 * @param orderId
	 * @param ruleSetType
	 * @param ruleName
	 * @param status
	 */
	createNewRule(
		orderId: number,
		ruleSetType: string,
		newRule: any,
		targets: string[]
	): Observable<any> {
		const url = `${this.desktopUrlRules}${orderId}/${ruleSetType}`;
		const payload = {
			messageType: newRule.messageType,
			displayText: newRule.title,
			message: newRule.description,
			severity: 'High',
			category: 'Unknown',
			ruleType: 'Checklist',
			targets: targets,
			sortId: newRule.sortId,
			uiSection: 'NONE'
		};
		return this.httpClient.post(url, payload);
	}

	/**
	 * PUT - Update rule status.
	 * @param orderId
	 * @param ruleSetType
	 * @param ruleName
	 * @param status
	 */
	updateRuleStatus(
		orderId: number,
		ruleSetType: string,
		ruleName: string,
		status: RULE_STATUS
	): Observable<any> {
		const url = `${this.desktopUrlRules}${orderId}/${ruleSetType}/${ruleName}`;
		const payload = {
			status: status
		};
		return this.httpClient.put(url, payload);
	}

	/**
	 * POST - Post a Rule Note
	 * @param orderId
	 * @param ruleSetType
	 * @param ruleName
	 * @param status
	 */
	postRuleNote(
		orderId: number,
		ruleSetType: string,
		ruleName: string,
		note: string,
		targets: string[]
	): Observable<any> {
		const url = `${this.desktopUrlRules}${orderId}/${ruleSetType}/${ruleName}/note`;
		const payload = {
			note: note,
			targets: targets
		};
		return this.httpClient.post(url, payload);
	}

	/**
	 * DELETE - Delete a Rule Note
	 * @param orderId
	 * @param ruleSetType
	 * @param ruleName
	 * @param noteId
	 */
	deleteRuleNote(
		orderId: number,
		ruleSetType: string,
		ruleName: string,
		noteId: string
	): Observable<any> {
		const url = `${this.desktopUrlRules}${orderId}/${ruleSetType}/${ruleName}/note?noteId=${noteId}`;
		return this.httpClient.delete(url);
	}

	/**
	 * POST - Post a Rule Stip
	 * @param orderId
	 * @param ruleSetType
	 * @param ruleName
	 * @param status
	 */
	postRuleStip(
		orderId: number,
		ruleSetType: string,
		ruleName: string,
		stipId: string,
		stip: string
	): Observable<any> {
		const url = `${this.desktopUrlRules}${orderId}/${ruleSetType}/${ruleName}/stip`;
		const payload = {
			id: stipId,
			stipulation: stip,
			overwritten: true
		};
		return this.httpClient.post(url, payload);
	}

	/**
	 * DELETE - Delete a Rule a Stip
	 * @param orderId
	 * @param ruleSetType
	 * @param ruleName
	 * @param stipId
	 */
	deleteStip(
		orderId: number,
		ruleSetType: string,
		ruleName: string,
		stipId: string
	): Observable<any> {
		const url = `${this.desktopUrlRules}${orderId}/${ruleSetType}/${ruleName}/stip?stipId=${stipId}`;
		return this.httpClient.delete(url);
	}

	/**
	 * POST - Revalidate GSE ruleset
	 * Automatic ruleset selection handled by backend
	 */
	revalidateGSE(orderId: string): Observable<any> {
		const url = `${this.desktopUrlOrders}${orderId}/gse/validate`;

		return this.httpClient.post(url, {}).pipe(
			map((response: any) => response)
		);
	}

	/**
	 * Get the Public Record by the Order and ID
	 * @param orderId
	 */
	public getPublicRecordByOrderId(orderId: any): Observable<any> {
		return this.httpClient.get(`${this.desktopUrlOrders}${orderId}/public-record`);
	}

	/**
	 * Select Public Record by the Order and ID
	 * @param orderId
	 * @param publicRecordId
	 */
	public selectPublicRecordByOrderId(orderId: any, publicRecordId: string): Observable<any> {
		const url = `${this.desktopUrlOrders}${orderId}/public-record`
		return this.httpClient.post(url, {id: publicRecordId});
	}

	/**Event to get local File in order to put in Smart-tag File */

	public getBlankImageSmartTag(): Promise<Blob> {
		return lastValueFrom(this.httpClient.get('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAQAAADa613fAAAAaUlEQVR42u3PQREAAAgDINc/9Izg34MGpJ0XIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiJyWYprx532021aAAAAAElFTkSuQmCC', {responseType: 'blob'}));
	}

	/**
	 * Event to convert base64Image in blob
	 * @param base64Image
	 */
	public getImageFromBase64(base64Image: string): Promise<Blob> {
		return lastValueFrom(this.httpClient.get(base64Image, {responseType: 'blob'}));
	}

	/**
	 *
	 * @param reportPath
	 * @param bodyData
	 * @returns
	 */
	public reportRequest(reportPath: string, bodyData: string): Observable<any> {
		const postData = {
			path: reportPath,
			ctx: bodyData
		}

		return this.httpClient.post(
			`${this.desktopUrlUtils}/report-request`,
			postData
		).pipe(
			map((resp: any) => {
				return resp;
			})
		);
	}

	/**
	 * Request floorplan revision endpoint
	 *
	 * @param orderId
	 * @returns
	 */
	public floorplanRevisionRequest(orderId: string): Observable<any> {
		const url = `${this.desktopUrlOrders}${orderId}/request-vendor-action-required`;
		return this.httpClient.post(url, {}).pipe(
			map((response: any) => response)
		);
	}

	/**
	 * POST - Sends rule feedback.
	 * @param {number} orderId
	 * @param {string} documentId
	 * @param {string} ruleId
	 * @param {string} feedback
	 * @returns {Observable<any>}
	 */
	public sendRuleFeedBack(
		orderId: number,
		ruleSetId: string,
		ruleName: string,
		feedback: string,
	): Observable<any> {
		const feedbackPayload = {
			ruleSetId,
			ruleName,
			feedbackMessage: feedback,
			feedbackReason: 'Other'
		}
		const url= `${this.desktopUrlRules}${orderId}/rule-feedback`;
		return this.httpClient.post(url, feedbackPayload);
	}

	/**
	 * Get a Blob From a Static Image
	 * @param request @see StaticMapRequest
	 * @param config @see StaticMapConfig
	 */
	public getStaticImageAsBlob(request: StaticMapRequest, config: StaticMapConfig = null): Observable<Blob> {
		// Use Default Configuration
		const defaultConfig = new StaticMapConfig();
		const mapConfig: StaticMapConfig = (config !== null) ? { ...defaultConfig, ...config } : defaultConfig;

		// Prepare Request
		let params = null;
		params = new HttpParams()
			.set('center', `${(request.address) ? request.address : request.location.lat},${request.location.lng}`)
			// .set('zoom', mapConfig.zoom) I found that with the zoom the calculation because tricky
			.set('size', mapConfig.size)
			.set('scale', mapConfig.scale)
			.set('maptype', mapConfig.type);

		// Main Marker can be added only if it is not an Address
		if (!request.address) {
			params = params.append('markers', `icon:${mapConfig.homeIcon}|${request.location.lat},${request.location.lng}`);
		}
		// Add Markers
		request.markers.forEach((marker: StaticMarker) => {
			let markerConfig = (marker.icon) ? `icon:${marker.icon}` : `color:${marker.color}`;
			markerConfig += `|${marker.location.lat},${marker.location.lng}`;
			params = params.append('markers', markerConfig);
		});

		const queryString = `${params.toString()}&key=${this.configService.publicKey.googleMaps}`;
		const url = `${this.systemApiService.api.desktopService}utils/staticmap?${queryString}`;
		return this.httpClient.get(url, { responseType: 'blob' });
	}

}
